// MF-576 — The roles editor (Maestro only): the permissions of the school's internal roles as a matrix of checkboxes.
// A change is kept in the page until "Save"; each role is saved on its own and the school's edits survive the roles
// sync. A cell that differs from what the seeder gives is highlighted, and a role can be put back to the seeder.
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { getRolesEditor, saveRoleEditor, resetRoleEditor } from '../../../../../services/roles/roles.service'
import { humanize, replaceDash } from '../../../../../utils/text'

const HIGHLIGHT = '#fff4e0' // a cell that differs from the seeder
const ROW_ONE = 64 // height of the role names row: the Save row sticks right under it

const key = (module, action) => `${module}::${action}`

const RolesEditor = ({ onClose }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [state, setState] = useState({ loading: true, error: null, data: null })
  const [edits, setEdits] = useState({}) // { roleId: { 'Module::action': true|false } }
  const [busy, setBusy] = useState(null)

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }))
    try {
      const res = await getRolesEditor()
      setState({ loading: false, error: null, data: res.data })
      setEdits({})
    } catch (err) {
      setState({ loading: false, error: err?.message || 'Could not load the permissions', data: null })
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const roles = useMemo(() => state.data?.roles || [], [state.data])
  const modules = useMemo(() => state.data?.modules || [], [state.data])
  const descriptions = state.data?.descriptions || { modules: {}, roles: {} }

  const isOn = (module, action, role) => {
    const edited = edits[role._id]?.[key(module.name, action)]
    return edited !== undefined ? edited : module.roles[role._id]?.on?.includes(action)
  }
  const isBase = (module, action, role) => module.roles[role._id]?.base?.includes(action)

  const toggle = (module, action, role, value) =>
    setEdits((current) => {
      const mine = { ...(current[role._id] || {}) }
      const original = module.roles[role._id]?.on?.includes(action)
      if (value === original) delete mine[key(module.name, action)]
      else mine[key(module.name, action)] = value
      const next = { ...current, [role._id]: mine }
      if (Object.keys(mine).length === 0) delete next[role._id]
      return next
    })

  // { moduleName: [actions that should be ON] } for the modules this role changed
  const selectionFor = (role) => {
    const changedModules = new Set(Object.keys(edits[role._id] || {}).map((k) => k.split('::')[0]))
    return Object.fromEntries(
      modules
        .filter((m) => changedModules.has(m.name))
        .map((m) => [m.name, m.actions.filter((a) => isOn(m, a, role))])
    )
  }

  const save = async (role) => {
    setBusy(role._id)
    try {
      const res = await saveRoleEditor(role._id, selectionFor(role))
      enqueueSnackbar(
        `${role.name} saved. ${res.data?.usersRefreshed ?? 0} users will get it on their next request`,
        {
          variant: 'success'
        }
      )
      await load()
    } catch (err) {
      enqueueSnackbar(err?.message || 'Could not save', { variant: 'error' })
    } finally {
      setBusy(null)
    }
  }

  const reset = async (role) => {
    // eslint-disable-next-line no-alert
    if (
      !window.confirm(
        `Put ${role.name} back to the standard permissions? The school's edits to this role are removed.`
      )
    )
      return
    setBusy(role._id)
    try {
      await resetRoleEditor(role._id)
      enqueueSnackbar(`${role.name} is back to the standard permissions`, { variant: 'success' })
      await load()
    } catch (err) {
      enqueueSnackbar(err?.message || 'Could not reset', { variant: 'error' })
    } finally {
      setBusy(null)
    }
  }

  if (state.loading && !state.data)
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    )
  if (state.error) return <Alert severity='error'>{state.error}</Alert>

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography sx={{ fontWeight: 700, flex: 1 }}>Edit permissions of this school</Typography>
        <Button size='small' onClick={onClose} sx={{ textTransform: 'none' }}>
          Back to the list
        </Button>
      </Box>
      <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1.5 }}>
        Only the modules the school has a license for appear. Tick or untick, then save the role. Highlighted
        cells differ from the standard permissions. The roles sync does not undo these edits, and the users of
        a role get the change on their next request (the menu updates when they sign in again). Maestro,
        Student and Parent cannot be edited.
      </Typography>
      <Box
        sx={{
          overflow: 'auto',
          maxHeight: 'calc(100vh - 290px)',
          minHeight: 320,
          border: '1px solid #e0e4ea',
          borderRadius: '8px'
        }}
      >
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 220, fontWeight: 700, top: 0, zIndex: 3, height: ROW_ONE }}>
                Module / action
              </TableCell>
              {roles.map((role) => (
                <TableCell
                  key={role._id}
                  align='center'
                  sx={{ minWidth: 120, fontWeight: 700, top: 0, zIndex: 3, height: ROW_ONE, py: 0.5 }}
                >
                  <Tooltip title={descriptions.roles?.[role.id] || ''} placement='top'>
                    <div>{role.name}</div>
                  </Tooltip>
                  {role.customized ? (
                    <Chip size='small' label='edited' sx={{ height: 18, fontSize: 10 }} />
                  ) : null}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell sx={{ top: ROW_ONE, zIndex: 3 }} />
              {roles.map((role) => {
                const dirty = Object.keys(edits[role._id] || {}).length > 0
                return (
                  <TableCell key={role._id} align='center' sx={{ top: ROW_ONE, zIndex: 3 }}>
                    {role.editable ? (
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <Button
                          size='small'
                          variant='contained'
                          disabled={!dirty || busy === role._id}
                          onClick={() => save(role)}
                          sx={{ textTransform: 'none', minWidth: 0, px: 1 }}
                        >
                          Save
                        </Button>
                        {role.customized ? (
                          <Tooltip title='Back to the standard permissions'>
                            <span>
                              <Button
                                size='small'
                                disabled={busy === role._id}
                                onClick={() => reset(role)}
                                sx={{ textTransform: 'none', minWidth: 0, px: 1 }}
                              >
                                Reset
                              </Button>
                            </span>
                          </Tooltip>
                        ) : null}
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>not editable</Typography>
                    )}
                  </TableCell>
                )
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {modules.map((module) => [
              <TableRow key={module.name}>
                <TableCell colSpan={roles.length + 1} sx={{ bgcolor: '#f3f6fa' }}>
                  <Typography component='span' sx={{ fontWeight: 700 }}>
                    {humanize(module.name)}
                  </Typography>
                  {descriptions.modules?.[module.name] ? (
                    <Typography component='span' sx={{ ml: 1.5, fontSize: 12, color: 'text.secondary' }}>
                      {descriptions.modules[module.name]}
                    </Typography>
                  ) : null}
                </TableCell>
              </TableRow>,
              ...module.actions.map((action) => (
                <TableRow key={key(module.name, action)} hover>
                  <TableCell sx={{ pl: 3, textTransform: 'capitalize' }}>{replaceDash(action)}</TableCell>
                  {roles.map((role) => {
                    const on = isOn(module, action, role)
                    const differs = on !== Boolean(isBase(module, action, role))
                    return (
                      <TableCell
                        key={role._id}
                        align='center'
                        sx={{ p: 0, bgcolor: differs ? HIGHLIGHT : undefined }}
                      >
                        <Checkbox
                          size='small'
                          checked={Boolean(on)}
                          disabled={!role.editable || busy === role._id}
                          onChange={(e) => toggle(module, action, role, e.target.checked)}
                          inputProps={{ 'aria-label': `${role.name}: ${module.name} ${action}` }}
                        />
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))
            ])}
          </TableBody>
        </Table>
      </Box>
    </Box>
  )
}

export default RolesEditor
